import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { alertServerError,/* , showNotification */
showNotification} from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { FormFieldProps, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { TObject } from '../../../../../../infrastructure/type-extensions';
import { editPassportTitleConfigList, EditPassportTitleFieldKey } from './edit-passport-title-form.config';
import { IEventTitleAndDescription, IPassportSettingsConfigurationDAO } from '../../../../../../api/passport-settings-configuration.dao';
import { IEventsDAO } from '../../../../../../api/events.dao';
import { FORM_UPDATED_TEXT } from '../../../../../../constants/common-labels.constants';
import { convertBoolTypeForMixPanel, handleTrackingValueChanges, TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../global/rich-text-editor/utils/transpiler';
import UploadImageS3VM from '../../../shared/upload-image/upload-image-to-s3.viewmodel';

class EditPassportTitleFormVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    protected static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'EditPassportTitleFormVM', editPassportTitleConfigList);
            }
        }();
    }

    @mobx.observable
    private formDataCtrl: AbstractFormViewModelController;
    @mobx.observable
    private isLoading: boolean;
    @mobx.observable
    private isSaving = false;
    @mobx.observable
    private uploadThumbnailImageS3VM: UploadImageS3VM = null;

    private initialDataSnapshot: IEventTitleAndDescription | undefined;

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly intlShape: IntlShape,
        @IoC.Inject private readonly passportSettingsConfigurationDAO: IPassportSettingsConfigurationDAO = IoC.Container.get(IPassportSettingsConfigurationDAO),
        @IoC.Inject protected readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
    ) {

    }
    public get intl(): IntlShape { return this.intlShape; }
    public get loading(): boolean { return this.isLoading; }
    public get saving(): boolean { return this.isSaving; }
    public get formVM(): AbstractFormViewModelController | undefined { return this.formDataCtrl; }
    public get initialData(): IEventTitleAndDescription { return this.initialDataSnapshot; }
    public get thumbnailImageVM(): UploadImageS3VM { return this.uploadThumbnailImageS3VM; }

    public getFormFields = (keys: TObject<string>): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(keys)) {
            formFields[key] = this.formDataCtrl.getFormVmValuePropsByKey(key);
        }
        return formFields;
    };

    public submitForm = async (): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            return this.updatePassportConfiguration();
        }
    };

    @mobx.action
    public initialize = async (): Promise<void> => {
        mobx.runInAction(() => {
            this.isLoading = true;
            this.formDataCtrl = null;
        });
        try {
            const result = await this.passportSettingsConfigurationDAO.getPassportTitleConfiguration({
                eventId: this.eventId,
                accountId: this.accountId
            });
            this.initialDataSnapshot = result;
            mobx.runInAction(() => {
                this.formDataCtrl = EditPassportTitleFormVM.createFormVM(this.fromApiToPassportConfigurationDTO(result));
                this.initThumbnailImageVM(result);
            });
        } catch (e) {
            EditPassportTitleFormVM.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private updatePassportConfiguration = async (): Promise<boolean> => {
        try {
            mobx.runInAction(() => {
                this.isSaving = true;
            });
            const toDTO = this.toPassportConfigurationDTO();
            await this.passportSettingsConfigurationDAO.editPassportTitleConfiguration(
                {
                    accountId: this.accountId,
                    eventId: this.eventId,
                },
                toDTO
            );
            showNotification(this.intl.formatMessage(FORM_UPDATED_TEXT));
            this.trackEventTitleAndDescription(toDTO);
            const eventDetails = await this.eventsDAO.getEventDetails({ accountId: this.accountId, eventId: this.eventId });
            this.globalStorage.setEvent(eventDetails);
            mobx.runInAction(() => {
                this.initialDataSnapshot = toDTO;
            });
            return true;
        } catch (e) {
            EditPassportTitleFormVM.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    private initThumbnailImageVM = (data: IEventTitleAndDescription): void => {
        this.uploadThumbnailImageS3VM = new UploadImageS3VM(this.accountId, this.eventId, 'thumbnailImage', 'event');
        this.uploadThumbnailImageS3VM.addImage(
            data.thumbnailImageWithUrl?.assetUrl,
            data.thumbnailImage?.title,
            data.thumbnailImage?.objectKey
        );
    };

    private fromApiToPassportConfigurationDTO = (data: IEventTitleAndDescription): IEventTitleAndDescription => {
        return {
            eventTitleForPassport: data.eventTitleForPassport || '',
            eventPublicDescription: transpileInlineStylesToClasses(data.eventPublicDescription || ''),
            eventPrivateDescription: transpileInlineStylesToClasses(data.eventPrivateDescription || ''),
        };
    };

    private toPassportConfigurationDTO = (): IEventTitleAndDescription => {
        const thumbnailImage = this.uploadThumbnailImageS3VM.getImageToApi();
        return {
            eventTitleForPassport: (this.formDataCtrl.getValue(EditPassportTitleFieldKey.TITLE) as string).trim(),
            eventPublicDescription: transpileClassesToInlineStyles(this.formDataCtrl.getValue(EditPassportTitleFieldKey.PUBLIC_DESCRIPTION) as string),
            eventPrivateDescription: transpileClassesToInlineStyles(this.formDataCtrl.getValue(EditPassportTitleFieldKey.PRIVATE_DESCRIPTION) as string),
            ...thumbnailImage,
        };
    };

    private trackEventTitleAndDescription = (eventData: IEventTitleAndDescription): void => {
        this.trackingService.get().track(TRACKING_EVENTS.CONFIGURE_EVENT_TITLE_AND_DESCRIPTION, {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Account ID': this.accountId,
            'Event Name': this.globalStorage.getEvent()?.name,
            'Event ID': this.eventId,
            'Event Title': handleTrackingValueChanges(
                this.initialData.eventTitleForPassport,
                eventData.eventTitleForPassport
            ),
            'Public Description': handleTrackingValueChanges(
                this.initialData.eventPublicDescription,
                eventData.eventPublicDescription
            ),
            'Private Description': handleTrackingValueChanges(
                this.initialData.eventPrivateDescription,
                eventData.eventPrivateDescription
            ),
            'Event Thumbnail Present': convertBoolTypeForMixPanel(!!this.uploadThumbnailImageS3VM.image.objectKey),
            'Event Thumbnail Changed': convertBoolTypeForMixPanel(!!this.uploadThumbnailImageS3VM.getImageChanged),
        });
    };



}

export { EditPassportTitleFormVM };
